// Versão: 1.00
// Data..: 2026-09-24
// Ordenação de tabelas reutilizável: hook useSortable + cabeçalho clicável SortableTh.
// O clique alterna asc/desc; um segundo indicador mostra a direção atual.

import { useMemo, useState, type ReactNode } from 'react';

export type SortDir = 'asc' | 'desc';

export interface SortState<T> {
  key: keyof T | string | null;
  dir: SortDir;
  toggle: (key: keyof T | string, accessor?: (row: T) => unknown) => void;
  activeKey: keyof T | string | null;
}

// Compara dois valores tratando número, data (string ISO) e texto (pt-BR, sem case).
function compareValues(a: unknown, b: unknown): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return a === b ? 0 : a ? 1 : -1;
  const sa = String(a);
  const sb = String(b);
  // Datas ISO ordenam corretamente como string; números em texto usam localeCompare numérico.
  return sa.localeCompare(sb, 'pt-BR', { numeric: true, sensitivity: 'base' });
}

// Ordena uma lista conforme o estado atual. Aceita acessores por chave para valores derivados.
export function useSortable<T>(
  rows: T[],
  accessors?: Partial<Record<string, (row: T) => unknown>>,
): { sorted: T[]; sort: SortState<T> } {
  const [key, setKey] = useState<keyof T | string | null>(null);
  const [dir, setDir] = useState<SortDir>('asc');

  const toggle = (nextKey: keyof T | string) => {
    if (key === nextKey) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setKey(nextKey);
      setDir('asc');
    }
  };

  const sorted = useMemo(() => {
    if (!key) return rows;
    const accessor = accessors?.[key as string] ?? ((row: T) => (row as Record<string, unknown>)[key as string]);
    const copy = [...rows];
    copy.sort((ra, rb) => {
      const cmp = compareValues(accessor(ra), accessor(rb));
      return dir === 'asc' ? cmp : -cmp;
    });
    return copy;
    // accessors é estável na prática (definido inline por render); dependemos de rows/key/dir.
  }, [rows, key, dir]); // eslint-disable-line react-hooks/exhaustive-deps

  return { sorted, sort: { key, dir, toggle, activeKey: key } };
}

// Cabeçalho <th> clicável que dispara a ordenação por `field`.
export function SortableTh<T>({
  field,
  sort,
  children,
  className,
}: {
  field: keyof T | string;
  sort: SortState<T>;
  children: ReactNode;
  className?: string;
}) {
  const active = sort.activeKey === field;
  const indicator = active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕';
  return (
    <th
      className={`sortable ${className ?? ''}`}
      onClick={() => sort.toggle(field)}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {children}
      <span className={`sort-ind ${active ? '' : 'dim'}`}>{indicator}</span>
    </th>
  );
}
